import { omit, omitBy } from 'lodash'
import { useMemo } from 'react'
import { FormatDateOptions } from 'react-intl'
import { Intl, Temporal, toTemporalInstant } from 'temporal-polyfill'
import { StringWithSuggestions } from '../../../common/types'
import { useIntlContext } from '../../../components/IntlProviders.context'

export type TemporalInstance =
  | Temporal.Instant
  | Temporal.PlainDate
  | Temporal.PlainDateTime
  | Temporal.PlainTime
  | Temporal.ZonedDateTime
  | Temporal.PlainYearMonth
  | Temporal.PlainMonthDay

export type TemporalClass =
  | typeof Temporal.Instant
  | typeof Temporal.PlainDate
  | typeof Temporal.PlainDateTime
  | typeof Temporal.PlainTime
  | typeof Temporal.ZonedDateTime
  | typeof Temporal.PlainYearMonth
  | typeof Temporal.PlainMonthDay

export type DateTimeDisplayInput = Date | TemporalInstance | string | number | null | undefined

export type DateDisplayInput = Exclude<DateTimeDisplayInput, Temporal.PlainTime>
export type TimeDisplayInput = Exclude<
  DateTimeDisplayInput,
  Temporal.PlainDate | Temporal.PlainYearMonth | Temporal.PlainMonthDay
>

/**
 * Date Display Overview
 *
 * Import the display components and hook from the design system
 *
 * import {
 *   DateTimeDisplay,
 *   DateDisplay,
 *   TimeDisplay,
 *   YearMonthDisplay,
 *   MonthDayDisplay,
 *   WeekdayDisplay,
 *   DateTimeRangeDisplay,
 *   useDateFormatters
 *  } from '~/design_system'
 *
 * You can render the display components as follows, e.g.
 *
 * <DateTimeDisplay input="2021-01-31T15:30:00Z" /> // Jan 31, 2021, 3:30 PM
 * <DateDisplay input="2021-01-31" /> // Jan 31, 2021
 * <TimeDisplay input="15:30:00" /> // 3:30 PM
 * <YearMonthDisplay input="2021-01" /> // Jan 2021
 * <MonthDayDisplay input="2021-01-31" /> // Jan 31
 * <WeekdayDisplay input="2021-01-31" /> // Sunday
 * <DateTimeRangeDisplay start="2021-01-31T15:30:00Z" end="2021-02-01T16:30:00Z" /> // Jan 31, 2021, 3:30 PM - Feb 1, 2021, 4:30 PM
 *
 * Each Display component has a hook function counterpart, e.g.
 *
 * const dateFmt = useDateFormatters()
 * dateFmt.formatDateTime("2021-01-31T15:30:00Z") // Jan 31, 2021, 3:30 PM
 * dateFmt.formatDate("2021-01-31") // Jan 31, 2021
 * dateFmt.formatTime("15:30:00") // 3:30 PM
 * dateFmt.formatYearMonth("2021-01") // Jan 2021
 * dateFmt.formatMonthDay("2021-01-31") // Jan 31
 * dateFmt.formatWeekday("2021-01-31") // Sunday
 * dateFmt.formatDateTimeRange("2021-01-31T15:30:00Z", "2021-02-01T16:30:00Z") // Jan 31, 2021, 3:30 PM - Feb 1, 2021, 4:30 PM
 *
 * The `input` can be a Temporal object, ISO-date string, legacy Date object (recommended to avoid), or epoch milliseconds.
 *
 * <DateTimeDisplay input="2021-01-31T15:30:00Z" /> // Jan 31, 2021, 3:30 PM
 * <DateTimeDisplay input={new Temporal.PlainDateTime(2021, 1, 31, 15, 30)} /> // Jan 31, 2021, 3:30 PM
 * <DateTimeDisplay input={1612102200000} /> // Jan 31, 2021, 3:30 PM
 * <DateTimeDisplay input={new Date("2021-01-31T15:30:00Z")} /> // Jan 31, 2021, 3:30 PM
 *
 * Temporal Objects are a great way to represent dates and times in JavaScript.
 * They are immutable and have built-in methods for manipulating dates and times.
 * They replace the legacy Date object and are more accurate and easier to work with.
 * We recommend using Temporal instead of date-fns to perform date and time calculations or manipulations.
 */

/**
 * Display a date and time in the user's locale and timeZone, e.g. "Jan 31, 2021, 3:30 PM"
 *
 * @param input {DateTimeDisplayInput} - The date to format
 * @param options {CustomFormatDateTimeOptions} - Additional options to format the date
 * @param options.date {boolean | 'numeric'} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will only show `date` (unless `time` is also true). If false, will hide the `date`. Format defaults to Jan 31, 2021. If set to 'numeric' will show 01/31/21.
 * @param options.time {boolean} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will show only `time` (unless `date` is also true). If false, will hide the `time`. Format is 3:30 PM.
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31, 2021, 3:30 PM" (short) / "Sunday, Jan 31, 2021, 3:30 PM" (long)
 * @param options.second {boolean} - If true, will show the `seconds`. If false/undefined, will not show the `seconds`. e.g. "Jan 31, 2021, 3:30:00 PM"
 * @param options.timeZoneName {boolean} - If true, will show the `timeZone`. If false/undefined, will not show the `timeZone`. e.g. "Jan 31, 2021, 3:30 PM EST"
 * @param options.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
 * @param options.year {boolean} - If true, will show the `year`. If false, will hide the `year`. If undefined, will only show if `date` is true.
 * @param options.month {boolean} - If true, will show the `month`. If false, will hide the `month`. If undefined, will only show if `date` is true.
 * @param options.day {boolean} - If true, will show the `day`. If false, will hide the `day`. If undefined, will only show if `date` is true.
 */
export function DateTimeDisplay({ input, ...options }: { input: DateTimeDisplayInput } & CustomFormatDateTimeOptions) {
  return <>{useDateFormatters().formatDateTime(input, options)}</>
}
/**
 * Display a date only in the user's locale and timeZone, e.g. "Jan 31, 2021"
 *
 * @param input {DateDisplayInput} - The date to format
 * @param options {CustomFormatDateOptions} - Additional options to format the date
 * @param options.date {'numeric' | undefined} - If undefined without any other options set, then the date will show. Format defaults to Jan 31, 2021. If set to 'numeric' will show 01/31/21.
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31, 2021" (short) / "Sunday, Jan 31, 2021" (long)
 * @param options.year {boolean} - If true, will show the `year`. If false, will hide the `year`. If undefined, will only show if `date` is true.
 * @param options.month {boolean} - If true, will show the `month`. If false, will hide the `month`. If undefined, will only show if `date` is true.
 * @param options.day {boolean} - If true, will show the `day`. If false, will hide the `day`. If undefined, will only show if `date` is true.
 * @param options.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
 */
export function DateDisplay({ input, ...options }: { input: DateDisplayInput } & CustomFormatDateOptions) {
  return <>{useDateFormatters().formatDate(input, options)}</>
}

/**
 * Display a time only in the user's locale and timeZone, e.g. "3:30 PM"
 *
 * @param input {TimeDisplayInput} - The date to format
 * @param options {CustomFormatTimeOptions} - Additional options to format the date
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. You must provide an input value that contains a date. e.g. "Sun 3:30 PM" (short) / "Sunday 3:30 PM" (long)
 * @param options.second {boolean} - If true, will show the `seconds`. If false/undefined, will not show the `seconds`.
 * @param options.timeZoneName {boolean} - If true, will show the `timeZone`. If false/undefined, will not show the `timeZone`.
 * @param options.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
 */
export function TimeDisplay({ input, ...options }: { input: TimeDisplayInput } & CustomFormatTimeOptions) {
  return <>{useDateFormatters().formatTime(input, options)}</>
}

/**
 * Display a year-month in the user's locale and timeZone, e.g. "Jan 2021"
 *
 * @param input {DateDisplayInput} - The date to format
 */
export function YearMonthDisplay({ input }: { input: DateDisplayInput }) {
  return <>{useDateFormatters().formatYearMonth(input)}</>
}

/**
 * Display a month-day in the user's locale and timeZone, e.g. "Jan 31"
 *
 * @param input {DateDisplayInput} - The date to format
 * @param options {CustomFormatDateOptions} - Additional options to format the date
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31" (short) / "Sunday, Jan 31" (long)
 */
export function MonthDayDisplay({ input, ...options }: { input: DateDisplayInput } & CustomFormatDateOptions) {
  return <>{useDateFormatters().formatMonthDay(input, options)}</>
}

/**
 * Display a weekday in the user's locale and timeZone, e.g. "Sunday"
 *
 * @param input {DateDisplayInput} - The date to format
 * @param options {CustomFormatDateOptions} - Additional options to format the date
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g "Sun" (short) / "Sunday" (long)
 */
export function WeekdayDisplay({ input, ...options }: { input: DateDisplayInput } & CustomFormatDateOptions) {
  return <>{useDateFormatters().formatWeekday(input, options)}</>
}

/**
 * Display a date, time, or datetime range in the user's locale and timeZone
 * date + time: e.g. "Jan 31, 2021, 3:30 PM - 4:30 PM"
 * date: e.g. "Jan 31, 2021 - Feb 1, 2021"
 * time: e.g. "3:30 PM - 4:30 PM"
 *
 * @param start {DateTimeDisplayInput} - The start date to format
 * @param end {DateTimeDisplayInput} - The end date to format
 * @param options {CustomFormatDateTimeOptions} - Additional options to format the date
 * @param options.date {boolean | 'numeric'} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will only show `date` (unless `time` is also true). If false, will hide the `date`. Format defaults to Jan 31 - Feb 1, 2021. If set to 'numeric' will show 01/31/21 - 02/01/21.
 * @param options.time {boolean} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will show only `time` (unless `date` is also true). If false, will hide the `time`. Format is 3:30 PM - 4:30 PM. Note: The date will be forced to show if the range includes different dates.
 * @param options.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31 - Mon, Feb 1" (short) / "Sunday, Jan 31 - Monday, Feb 1" (long)
 * @param options.second {boolean} - If true, will show the `seconds`. If false/undefined, will not show the `seconds`.
 * @param options.timeZoneName {boolean} - If true, will show the `timeZone`. If false/undefined, will not show the `timeZone`.
 * @param options.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
 */
export function DateTimeRangeDisplay({
  start,
  end,
  ...options
}: { start: DateTimeDisplayInput; end: DateTimeDisplayInput } & CustomFormatDateTimeOptions) {
  return <>{useDateFormatters().formatDateTimeRange(start, end, options)}</>
}

/**
 * Returns date formatters already bound to the user's locale and timeZone
 */
export function useDateFormatters() {
  const { intl } = useIntlContext()
  return useMemo(() => {
    return getDateFormattersVanilla({ locale: intl.locale, timeZone: intl.timeZone })
  }, [intl])
}

type Formatted<TInput> = TInput extends null | undefined | '' ? null : TInput extends string ? string | null : string

/**
 * Returns date formatters that require you to manually bind the locale and timeZone
 */
export const getDateFormattersVanilla = (intl: LocaleAndTz) => {
  return {
    intl,
    /**
     * Formats a Temporal obj, Date, or date string to a datetime in the user's locale and timeZone, e.g. "Jan 31, 2021, 3:30 PM"
     *
     * @param dateInput {DateTimeDisplayInput} - The date to format
     * @param formatOptions {CustomFormatDateTimeOptions} - Additional options to format the date
     * @param formatOptions.date {boolean | 'numeric'} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will only show `date` (unless `time` is also true). If false, will hide the `date`. Format defaults to Jan 31, 2021. If set to 'numeric' will show 01/31/21.
     * @param formatOptions.time {boolean} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will show only `time` (unless `date` is also true). If false, will hide the `time`. Format is 3:30 PM.
     * @param formatOptions.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format.
     * @param formatOptions.second {boolean} - If true, will show the `seconds`. If false/undefined, will not show the `seconds`.
     * @param formatOptions.timeZoneName {boolean} - If true, will show the `timeZone`. If false/undefined, will not show the `timeZone`.
     * @param formatOptions.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
     * @param formatOptions.year {boolean} - If true, will show the `year`. If false, will hide the `year`. If undefined, will only show if `date` is true.
     * @param formatOptions.month {boolean} - If true, will show the `month`. If false, will hide the `month`. If undefined, will only show if `date` is true.
     * @param formatOptions.day {boolean} - If true, will show the `day`. If false, will hide the `day`. If undefined, will only show if `date` is true.
     */
    formatDateTime: <TInput extends DateTimeDisplayInput>(
      dateInput: TInput,
      formatOptions?: CustomFormatDateTimeOptions
    ): Formatted<TInput> => formatDateTime(intl, dateInput, formatOptions) as Formatted<TInput>,
    /**
     * Formats a Temporal obj, Date, or date string to a date only in the user's locale and timeZone, e.g. "Jan 31, 2021"
     *
     * @param dateInput {DateDisplayInput} - The date to format
     * @param formatOptions {CustomFormatDateOptions} - Additional options to format the date
     * @param formatOptions.date {'numeric' | undefined} - If undefined without any other options set, then the date will show. Format defaults to Jan 31, 2021. If set to 'numeric' will show 01/31/21.
     * @param formatOptions.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format.
     * @param formatOptions.year {boolean} - If true, will show the `year`. If false, will hide the `year`. If undefined, will only show if `date` is true.
     * @param formatOptions.month {boolean} - If true, will show the `month`. If false, will hide the `month`. If undefined, will only show if `date` is true.
     * @param formatOptions.day {boolean} - If true, will show the `day`. If false, will hide the `day`. If undefined, will only show if `date` is true.
     * @param formatOptions.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
     */
    formatDate: <TInput extends DateDisplayInput>(
      dateInput: TInput,
      formatOptions?: CustomFormatDateOptions
    ): Formatted<TInput> => formatDate(intl, dateInput, formatOptions) as Formatted<TInput>,
    /**
     * Formats a Temporal obj, Date, or date string to a time only in the user's locale and timeZone, e.g. "3:30 PM"
     *
     * @param dateInput {TimeDisplayInput} - The date to format
     * @param formatOptions {CustomFormatTimeOptions} - Additional options to format the date
     * @param formatOptions.second {boolean} - If true, will show the `seconds`. If false/undefined, will not show the `seconds`.
     * @param formatOptions.timeZoneName {boolean} - If true, will show the `timeZone`. If false/undefined, will not show the `timeZone`.
     * @param formatOptions.timeZone {string} - The timeZone to use for formatting. If not provided, will use the user's profile timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
     */
    formatTime: <TInput extends TimeDisplayInput>(
      dateInput: TInput,
      formatOptions?: CustomFormatTimeOptions
    ): Formatted<TInput> => formatTime(intl, dateInput, formatOptions) as Formatted<TInput>,
    /**
     * Formats a Temporal obj, Date, or date string to a year-month in the user's locale and timeZone, e.g. "Jan 2021"
     *
     * @param dateInput {DateDisplayInput} - The date to format
     */
    formatYearMonth: <TInput extends DateDisplayInput>(dateInput: TInput): Formatted<TInput> =>
      formatYearMonth(intl, dateInput) as Formatted<TInput>,
    /**
     * Formats a Temporal obj, Date, or date string to a month-day in the user's locale and timeZone, e.g. "Jan 31"
     *
     * @param dateInput {DateDisplayInput} - The date to format
     * @param formatOptions {CustomFormatDateOptions} - Additional options to format the date
     * @param formatOptions.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31" (short) / "Sunday, Jan 31" (long)
     */
    formatMonthDay: <TInput extends DateDisplayInput>(
      dateInput: TInput,
      formatOptions?: Omit<CustomFormatDateOptions, 'date'>
    ): Formatted<TInput> => formatMonthDay(intl, dateInput, formatOptions) as Formatted<TInput>,
    /**
     * Formats a Temporal obj, Date, or date string to a weekday in the user's locale and timeZone, e.g. "Sunday"
     *
     * @param dateInput {DateDisplayInput} - The date to format
     * @param formatOptions {CustomFormatDateOptions} - Additional options to format the date
     * @param formatOptions.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun" (short) / "Sunday" (long)
     */
    formatWeekday: <TInput extends DateDisplayInput>(
      dateInput: TInput,
      formatOptions?: CustomFormatDateOptions
    ): Formatted<TInput> => formatWeekday(intl, dateInput, formatOptions) as Formatted<TInput>,
    /**
     * Formats a start and end Temporal objs, Dates, or date strings to a datetime range in the user's locale and timeZone, e.g. "Jan 31, 2021, 3:30 PM - Feb 1, 2021, 4:30 PM"
     *
     * @param startInput {DateTimeDisplayInput} - The start date to format
     * @param endInput {DateTimeDisplayInput} - The end date to format
     * @param formatOptions {CustomFormatDateTimeOptions} - Additional options to format the date
     * @param formatOptions.date {boolean} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will only show `date` (unless `time` is also true). If false, will hide the `date`. Format defaults to Jan 31 - Feb 1, 2021. If set to 'numeric' will show 01/31/21 - 02/01/21.
     * @param formatOptions.time {boolean} - If both `date` and `time` are undefined, will show both `date` and `time`. If true, will show only `time` (unless `date` is also true). If false, will hide the `time`. Format is 3:30 PM - 4:30 PM. Note: The date will be forced to show if the range includes different dates.
     * @param formatOptions.weekday {'short' | 'long'} - If provided, will show the `weekday` in the specified format. e.g. "Sun, Jan 31 - Mon, Feb 1" (short) / "Sunday, Jan 31 - Monday, Feb 1" (long)
     */
    formatDateTimeRange: <TInput extends DateTimeDisplayInput>(
      startInput: TInput,
      endInput: TInput,
      formatOptions?: CustomFormatDateTimeOptions
    ): Formatted<TInput> => formatDateTimeRange(intl, startInput, endInput, formatOptions) as Formatted<TInput>,

    /**
     * A set of utils to convert any Temporal obj, Date, or date string to ISO string format
     */
    toISO: {
      date: toISODateString,
      time: toISOTimeString,
      dateTime: toISODateTimeString,
      yearMonth: toISOYearMonthString
    },
    /**
     * Converts a Temporal obj, Date, or date string to a legacy Date object
     */
    toLegacyDate,
    /**
     * Converts a Date to a Temporal obj
     */
    fromLegacyDate: {
      toInstant: (d: Date) => toTemporalInstant.call(d),
      toPlainDate: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts).toPlainDate(),
      toZonedDateTime: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts),
      toPlainDateTime: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts).toPlainDateTime(),
      toPlainMonthDay: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts).toPlainMonthDay(),
      toPlainTime: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts).toPlainTime(),
      toPlainYearMonth: (d: Date, opts?: { timeZone: SystemOrProfileTimeZone }) =>
        fromLegacyDateToZonedDateTime(d, opts).toPlainYearMonth()
    }
  }

  /**
   * Returns a date string in ISO format (with the user's timeZone if applicable), e.g. "2021-01-31"
   *
   * @param input {TemporalInstanceOrHigher<Temporal.PlainDate> | Temporal.Instant | Date | number | string | null | undefined} - The date to format
   * @param opts {object} - Additional options to format the date
   * @param opts.timeZone {string} - The timeZone to use for formatting. If not provided, will use the system timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
   */
  function toISODateString(input: ISOStringInputs<Temporal.PlainDate>, opts?: { timeZone?: string }) {
    return handleBaseISOStringInputs(input, Temporal.PlainDate, resolveToSystemOrProfileTz(intl, opts?.timeZone))
  }

  type SmallestUnit = 'minutes' | 'seconds' | 'milliseconds'
  /**
   * Returns a time string in ISO format (with the user's timeZone if applicable), e.g. "15:30:00"
   *
   * @param input {TemporalInstanceOrHigher<Temporal.PlainTime> | Temporal.Instant | Date | number | string | null | undefined} - The date to format
   * @param opts {object} - Additional options to format the date
   * @param opts.timeZone {string} - The timeZone to use for formatting. If not provided, will use the system timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
   * @param opts.smallestUnit {'minutes' | 'seconds' | 'milliseconds'} - If provided, will show the `smallestUnit` in the specified format. Default is 'seconds'.
   */
  function toISOTimeString<TInput extends ISOStringInputs<Temporal.PlainTime>>(
    input: TInput,
    opts?: { timeZone?: string; smallestUnit?: SmallestUnit }
  ) {
    return _applySmallestUnitToISOTime(
      handleBaseISOStringInputs(input, Temporal.PlainTime, resolveToSystemOrProfileTz(intl, opts?.timeZone)),
      opts?.smallestUnit
    )
  }

  /**
   * Returns a date/time string in ISO format (with the user's timeZone if applicable), e.g. "2021-01-31T15:30:00"
   *
   * @param input {TemporalInstanceOrHigher<Temporal.PlainDateTime> | Temporal.Instant | Date | number | string | null | undefined} - The date to format
   * @param opts {object} - Additional options to format the date
   * @param opts.timeZone {'system' | 'profile' | string} - The timeZone to use for formatting. If not provided, will use the system timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
   * @param opts.smallestUnit {'minutes' | 'seconds' | 'milliseconds'} - If provided, will show the `smallestUnit` in the specified format. Default is 'seconds'.
   */
  function toISODateTimeString(
    input: ISOStringInputs<Temporal.PlainDateTime>,
    opts?: { timeZone?: string; smallestUnit?: SmallestUnit }
  ) {
    return _applySmallestUnitToISOTime(
      handleBaseISOStringInputs(input, Temporal.PlainDateTime, resolveToSystemOrProfileTz(intl, opts?.timeZone)),
      opts?.smallestUnit
    )
  }

  /**
   * Returns a year/month string in ISO format (with the user's timeZone if applicable), e.g. "2021-01"
   *
   * @param input {TemporalInstanceOrHigher<Temporal.PlainYearMonth> | Temporal.Instant | Date | number | string | null | undefined} - The date to format
   * @param opts {object} - Additional options to format the date
   * @param opts.timeZone {'system' | 'profile' | string} - The timeZone to use for formatting. If not provided, will use the system timeZone. If timeZone is provided, note that the date and time will shift to that timeZone.
   */
  function toISOYearMonthString(input: ISOStringInputs<Temporal.PlainYearMonth>, opts?: { timeZone?: string }) {
    if (input instanceof Temporal.PlainDate) return input.toPlainYearMonth().toString()
    return handleBaseISOStringInputs(input, Temporal.PlainYearMonth, resolveToSystemOrProfileTz(intl, opts?.timeZone))
  }

  function _applySmallestUnitToISOTime(input: string | null, smallestUnit?: SmallestUnit) {
    if (input == null) return null
    if (smallestUnit === 'minutes') return input?.replace(/(\d\d:\d\d):\d\d(\.\d{1,5})?/, '$1')
    if (smallestUnit === undefined || smallestUnit === 'seconds')
      return input?.replace(/(\d\d:\d\d:\d\d)(\.\d{1,5})?/, '$1')
    return input // smallestUnit === 'milliseconds'
  }

  function toLegacyDate(
    input: TemporalInstance | Date | string | number | null | undefined,
    opts: { timeZone?: SystemOrProfileTimeZone } = {}
  ): Date | null {
    let temporalObj: TemporalInstance | null = null
    if (!input) return null
    if (input instanceof Date) return input
    if (typeof input === 'string') {
      temporalObj = getTemporalInstanceFromISOString(input)
    }
    if (isTemporal(input)) {
      temporalObj = input
    }
    if (typeof input === 'number') {
      temporalObj = Temporal.Instant.fromEpochMilliseconds(input)
    }
    if (temporalObj instanceof Temporal.Instant || temporalObj instanceof Temporal.ZonedDateTime) {
      if (opts.timeZone) {
        throw new Error('You should not provide a timeZone for Instants, ZoneDateTimes, or Dates.')
      }
      return new Date(temporalObj.epochMilliseconds)
    }

    const tz = resolveToSystemOrProfileTz(intl, opts.timeZone as SystemOrProfileTimeZone)
    if (temporalObj instanceof Temporal.PlainDateTime) {
      return new Date(temporalObj.toZonedDateTime(tz).epochMilliseconds)
    }
    if (temporalObj instanceof Temporal.PlainDate) {
      return new Date(temporalObj.toZonedDateTime(tz).epochMilliseconds)
    }
    if (temporalObj instanceof Temporal.PlainTime) {
      return new Date(
        temporalObj.toZonedDateTime({
          plainDate: Temporal.Now.plainDateISO(tz),
          timeZone: tz
        }).epochMilliseconds
      )
    }
    if (temporalObj instanceof Temporal.PlainYearMonth) {
      return new Date(temporalObj.toPlainDate({ day: 1 }).toZonedDateTime(tz).epochMilliseconds)
    }
    if (temporalObj instanceof Temporal.PlainMonthDay) {
      return new Date(
        temporalObj.toPlainDate({ year: Temporal.Now.plainDateISO().year }).toZonedDateTime(tz).epochMilliseconds
      )
    }
    return null
  }

  function fromLegacyDateToZonedDateTime(date: Date, opts: { timeZone?: SystemOrProfileTimeZone } = {}) {
    return toTemporalInstant.call(date).toZonedDateTimeISO(resolveToSystemOrProfileTz(intl, opts.timeZone))
  }
}

function resolveToSystemOrProfileTz(intl: LocaleAndTz, timeZone: SystemOrProfileTimeZone) {
  // Default to system timeZone for now to maintain existing functionality.
  // We need to work with product to figure out if we should default to profile timeZone at some point in the future.
  if (timeZone === 'system' || timeZone === undefined) return Temporal.Now.timeZoneId()
  if (timeZone === 'profile') return intl.timeZone ?? Temporal.Now.timeZoneId()
  return timeZone
}

type LocaleAndTz = { locale: string; timeZone?: string }

function formatDate(intl: LocaleAndTz, dateInput: DateDisplayInput, formatOptions?: CustomFormatDateOptions) {
  return formatDateTime(intl, dateInput, {
    ...formatOptions,
    date: formatOptions?.date ?? true,
    time: false
  })
}

function formatTime(intl: LocaleAndTz, dateInput: TimeDisplayInput, formatOptions?: CustomFormatTimeOptions) {
  return formatDateTime(intl, dateInput, { ...formatOptions, date: false, time: true })
}

function formatYearMonth(intl: LocaleAndTz, dateInput: DateDisplayInput, formatOptions?: CustomFormatDateOptions) {
  return formatDateTime(intl, dateInput, {
    ...formatOptions,
    date: true,
    day: false,
    time: false
  })
}

function formatMonthDay(intl: LocaleAndTz, dateInput: DateDisplayInput, formatOptions?: CustomFormatDateOptions) {
  return formatDateTime(intl, dateInput, {
    ...formatOptions,
    date: true,
    year: false,
    time: false
  })
}

function formatWeekday(intl: LocaleAndTz, dateInput: DateDisplayInput, formatOptions?: CustomFormatDateOptions) {
  return formatDateTime(intl, dateInput, {
    weekday: 'long',
    ...formatOptions,
    date: false,
    time: false
  })
}

function formatDateTimeRange(
  intl: LocaleAndTz,
  startInput: DateTimeDisplayInput,
  endInput: DateTimeDisplayInput,
  formatOptions?: CustomFormatDateTimeOptions
) {
  const temporalStart = _parseToTemporal(startInput)
  const temporalEnd = _parseToTemporal(endInput)
  const timeZone = resolveToSystemOrProfileTz(intl, formatOptions?.timeZone)

  if (temporalStart && temporalEnd) {
    let opts: Intl.DateTimeFormatOptions
    if (formatOptions?.time && !formatOptions?.date) {
      const sharesSameDate =
        new Intl.DateTimeFormat(intl.locale, { timeZone })
          .formatRangeToParts(temporalStart, temporalEnd)
          .filter(part => part.type.match(/^year|month|day$/) && part.source === 'shared').length === 3
      if (sharesSameDate) {
        opts = toLocaleStringOpts(intl, { ...formatOptions, date: false })
      } else {
        opts = toLocaleStringOpts(intl, { ...formatOptions, date: true })
      }
    } else {
      opts = toLocaleStringOpts(intl, formatOptions)
    }
    return new Intl.DateTimeFormat(intl.locale, { timeZone, ...opts }).formatRange(temporalStart, temporalEnd)
  }
  if (temporalStart) {
    return formatDateTime(intl, temporalStart, formatOptions)
  }
  if (temporalEnd) {
    return formatDateTime(intl, temporalEnd, formatOptions)
  }
  return null
}

function formatDateTime(
  intl: LocaleAndTz,
  dateInput: DateTimeDisplayInput,
  formatOptions?: CustomFormatDateTimeOptions
) {
  const temporalObj = _parseToTemporal(dateInput)
  if (temporalObj == null) return null

  const opts = toLocaleStringOpts(intl, formatOptions)

  // Handle Temporal.Instant
  if (temporalObj instanceof Temporal.Instant) {
    // The only temporal object that needs a timeZone to print the locale string
    return temporalObj.toLocaleString(intl.locale, opts)
  }

  // Handle Temporal.ZonedDateTime
  if (temporalObj instanceof Temporal.ZonedDateTime) {
    return temporalObj.withTimeZone(opts.timeZone).toLocaleString(intl.locale, omit(opts, ['timeZone']))
  }

  const plainDateOpts = ['year', 'month', 'day', 'weekday']
  const plainTimeOpts = ['hour', 'minute', 'second']

  if (formatOptions?.timeZone)
    // eslint-disable-next-line no-console
    console.warn(
      `The provided timeZone of ${formatOptions.timeZone} will be ignored while formatting this input:`,
      dateInput
    )

  // Handle PlainDate
  if (temporalObj instanceof Temporal.PlainDate) {
    const hasDateOpts = Object.keys(opts).some(key => plainDateOpts.includes(key))
    if (!hasDateOpts) return ''
    const options = omit(opts, [...plainTimeOpts, 'timeZone'])
    return temporalObj.toLocaleString(intl.locale, options)
  }

  // Handle PlainTime
  if (temporalObj instanceof Temporal.PlainTime) {
    const hasTimeOpts = Object.keys(opts).some(key => plainTimeOpts.includes(key))
    if (!hasTimeOpts) return ''
    const options = omit(opts, [...plainDateOpts, 'timeZone'])
    return temporalObj.toLocaleString(intl.locale, options)
  }

  if (temporalObj instanceof Temporal.PlainYearMonth) {
    const hasYearMonthOpts = Object.keys(opts).some(key => ['year', 'month'].includes(key))
    if (!hasYearMonthOpts) return ''
    const options = omit(opts, [...plainTimeOpts, 'day', 'weekday', 'timeZone'])
    return temporalObj.toLocaleString(intl.locale, { ...options, calendar: temporalObj.calendarId })
  }

  if (temporalObj instanceof Temporal.PlainMonthDay) {
    const hasYearMonthOpts = Object.keys(opts).some(key => ['month', 'day'].includes(key))
    if (!hasYearMonthOpts) return ''
    const options = omit(opts, [...plainTimeOpts, 'year', 'weekday', 'timeZone'])
    return temporalObj.toLocaleString(intl.locale, { ...options, calendar: temporalObj.calendarId })
  }

  // Handle PlainDateTime
  return temporalObj.toLocaleString(intl.locale, omit(opts, ['timeZone']))
}

function toLocaleStringOpts(
  intl: LocaleAndTz,
  formatOptions?: CustomFormatDateTimeOptions
): Omit<Intl.DateTimeFormatOptions, 'timeZone'> & { timeZone: string } {
  const { date, time, timeZoneName, year, month, day, second, timeZone, ...nativeOpts } = { ...formatOptions }

  let customOpts = { date, time, timeZone, timeZoneName, year, month, day, second }
  let opts: Intl.DateTimeFormatOptions = { ...nativeOpts }

  if (customOpts.date === undefined && customOpts.time === undefined) {
    customOpts = { ...customOpts, date: true, time: true }
  }

  // Handle opts.timeZoneName
  if (customOpts.timeZoneName === true) {
    opts = { ...opts, timeZoneName: 'short' }
  } else if (customOpts.timeZoneName === false) {
    opts = omit(opts, ['timeZoneName'])
  }

  // Handle opts.second
  if (customOpts.second === true) {
    opts = { ...opts, second: 'numeric' }
  }

  // Handle date
  if (customOpts.date === true) {
    opts = { ...opts, year: opts.year ?? 'numeric', month: opts.month ?? 'short', day: opts.day ?? 'numeric' }
  } else if (customOpts.date === false) {
    opts = { ...opts, year: opts.year, month: opts.month, day: opts.day }
  } else if (customOpts.date === 'numeric') {
    opts = { ...opts, year: '2-digit', month: '2-digit', day: '2-digit' }
  }

  // Handles opts.year
  if (customOpts.year === true) {
    opts = { ...opts, year: 'numeric' }
  } else if (customOpts.year === false) {
    opts = omit(opts, ['year'])
  }

  // Handles opts.month
  if (customOpts.month === true) {
    opts = { ...opts, month: 'short' }
  } else if (customOpts.month === false) {
    opts = omit(opts, ['month'])
  } else if (customOpts.month === 'long') {
    opts = { ...opts, month: 'long' }
  }

  // Handles opts.day
  if (customOpts.day === true) {
    opts = { ...opts, day: 'numeric' }
  } else if (customOpts.day === false) {
    opts = omit(opts, ['day'])
  }

  // Handle opts.time
  if (customOpts.time === true) {
    opts = { ...opts, hour: opts.hour ?? 'numeric', minute: opts.minute ?? 'numeric' }
  } else if (customOpts.time === false) {
    opts = { ...opts, hour: opts.hour, minute: opts.minute, second: opts.second }
  }

  opts = omitBy(opts, value => value === undefined)

  const tz = resolveToSystemOrProfileTz(intl, customOpts.timeZone)

  if (Object.keys(opts).length === 0) {
    return { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZone: tz }
  }
  return { ...opts, timeZone: tz }
}

type SystemOrProfileTimeZone = StringWithSuggestions<'profile' | 'system'> | undefined

/**
 * Extended options for formatting dates.
 *
 * Adds some simple options to show date, time, and timeZone in our preferred formats.
 *
 * The date and time options are similar to dateStyle and timeStyle, but they play nicely
 * with other options that are not allowed when using the native dateStyle and timeStyle.
 */
export interface CustomFormatDateTimeOptions
  extends Omit<
    FormatDateOptions,
    'timeZoneName' | 'month' | 'day' | 'year' | 'second' | 'dateStyle' | 'timeStyle' | 'timeZone'
  > {
  /**
   * Defaults to Jan 31, 2021. If set to 'numeric' will show 01/31/21.
   * If undefined without any other options set, then the date will show.
   */
  date?: boolean | 'numeric'
  /**
   * If true, will show the time. If false/undefined, will not show the time.
   * If true and date is undefined, will only show the time without the date.
   */
  time?: boolean
  /**
   * If true, will show the timeZone. If false/undefined, will not show the timeZone.
   */
  timeZoneName?: boolean | FormatDateOptions['timeZoneName']
  /**
   * The timeZone to use for formatting. If not provided, will use the user's profile timeZone.
   * If `'system'` is provided, will use the system timeZone, aka browser's timeZone.
   * If `'profile'` is provided, will use the user's profile timeZone, same as default.
   * If a string is provided, will use that specific timeZone, e.g. 'America/New_York'.
   */
  timeZone?: SystemOrProfileTimeZone
  /**
   * If true, will show the year. If false, will hide the year. If undefined, will only show if date is true.
   */
  year?: boolean | FormatDateOptions['year']
  /**
   * If true, will show the month in short form. If false, will hide the month. If undefined, will only show if date is true. If 'long', will show the full month.
   */
  month?: boolean | FormatDateOptions['month']
  /**
   * If true, will show the day. If false, will hide the day. If undefined, will only show if date is true.
   */
  day?: boolean | FormatDateOptions['day']
  /**
   * If true, will show the seconds. If false/undefined, will not show the seconds.
   */
  second?: true | FormatDateOptions['second']
}

export type CustomFormatDateOptions = Omit<
  CustomFormatDateTimeOptions,
  | 'date'
  | 'time'
  | 'timeZoneName'
  | 'timeStyle'
  | 'hour'
  | 'minute'
  | 'second'
  | 'hour12'
  | 'hourCycle'
  | 'fractionalSecondDigits'
  | 'dayPeriod'
> & {
  date?: 'numeric'
}

export type CustomFormatTimeOptions = Omit<
  CustomFormatDateTimeOptions,
  'date' | 'time' | 'dateStyle' | 'month' | 'day' | 'year' | 'era'
>

type TemporalInstantISOString =
  `${number}${number}${number}${number}-${number}-${number}T${number}:${number}:${number}.${number}${number}${number}Z`
type TemporalPlainDateTimeISOString =
  | `${number}${number}${number}${number}-${number}-${number}T${number}:${number}:${number}`
  | `${number}${number}${number}${number}-${number}-${number}T${number}:${number}`
type TemporalPlainDateISOString = `${number}${number}${number}${number}-${number}-${number}`
type TemporalPlainTimeISOString = `${number}:${number}:${number}` | `${number}:${number}`
type TemporalZonedDateTimeISOString =
  `${number}${number}${number}${number}-${number}-${number}T${number}:${number}:${number}.${number}${number}${number}${
    | '+'
    | '-'}${number}${number}:${number}${number}[${string}]`
type TemporalPlainYearMonthISOString = `${number}${number}${number}${number}-${number}`
type TemporalPlainMonthDayISOString = `${number}-${number}${number}`

type ParsedTemporal<TInput extends DateTimeDisplayInput> = TInput extends null | undefined | ''
  ? null
  : TInput extends TemporalInstantISOString
  ? Temporal.Instant
  : TInput extends TemporalZonedDateTimeISOString
  ? Temporal.ZonedDateTime
  : TInput extends TemporalPlainDateTimeISOString
  ? Temporal.PlainDateTime
  : TInput extends TemporalPlainDateISOString
  ? Temporal.PlainDate
  : TInput extends TemporalPlainTimeISOString
  ? Temporal.PlainTime
  : TInput extends TemporalPlainYearMonthISOString
  ? Temporal.PlainYearMonth
  : TInput extends TemporalPlainMonthDayISOString
  ? Temporal.PlainMonthDay
  : TInput extends string
  ? TemporalInstance
  : TInput extends number | Date
  ? Temporal.Instant
  : TInput extends TemporalInstance
  ? TInput
  : null

const strRegex = {
  isPlainDateTime: (input: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(input),
  isPlainDate: (input: string) => /^\d{4}-\d{2}-\d{2}$/.test(input),
  isPlainTime: (input: string) => /^\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(input),
  isZonedDateTime: (input: string) => /\[.*]$/.test(input),
  isYearMonth: (input: string) => /^\d{4}-\d{2}$/.test(input),
  isMonthDay: (input: string) => /^\d{2}-\d{2}$/.test(input),
  isInstant: (input: string) => input.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(input)
}

function getTemporalClassFromISOString(input: string): TemporalClass | null {
  if (strRegex.isPlainDateTime(input)) return Temporal.PlainDateTime
  if (strRegex.isPlainDate(input)) return Temporal.PlainDate
  if (strRegex.isPlainTime(input)) return Temporal.PlainTime
  if (strRegex.isZonedDateTime(input)) return Temporal.ZonedDateTime
  if (strRegex.isYearMonth(input)) return Temporal.PlainYearMonth
  if (strRegex.isMonthDay(input)) return Temporal.PlainMonthDay
  if (strRegex.isInstant(input)) return Temporal.Instant
  return null
}

const getTemporalInstanceFromISOString = (input: string): TemporalInstance | null => {
  const TemporalClass = getTemporalClassFromISOString(input)

  if (!TemporalClass) {
    // eslint-disable-next-line no-console
    console.error('getTemporalInstanceFromISOString: Invalid ISO String,', input)
    return null
  }

  return TemporalClass.from(input)
}

export const _parseToTemporal = <TInput extends DateTimeDisplayInput>(input: TInput): ParsedTemporal<TInput> => {
  if (!input) {
    return null as ParsedTemporal<TInput>
  }

  if (isTemporal(input)) {
    return input as ParsedTemporal<TInput>
  }

  if (typeof input === 'number' || input instanceof Date) {
    return Temporal.Instant.fromEpochMilliseconds(new Date(input).getTime()) as ParsedTemporal<TInput>
  }

  return getTemporalInstanceFromISOString(input) as ParsedTemporal<TInput>
}

export type ISOStringInputs<
  T extends Temporal.PlainDate | Temporal.PlainDateTime | Temporal.PlainTime | Temporal.PlainYearMonth
> = TemporalInstanceOrHigher<T> | string | Date | number | Temporal.Instant | undefined | null

function handleBaseISOStringInputs<
  TClass extends
    | typeof Temporal.PlainDate
    | typeof Temporal.PlainDateTime
    | typeof Temporal.PlainTime
    | typeof Temporal.PlainYearMonth
>(_input: ISOStringInputs<InstanceType<TClass>>, TargetTemporalClass: TClass, timeZone?: string) {
  let input = _input

  const conversionFn = new Map<TemporalClass, 'toPlainDate' | 'toPlainDateTime' | 'toPlainTime' | 'toPlainYearMonth'>([
    [Temporal.PlainDate, 'toPlainDate'],
    [Temporal.PlainDateTime, 'toPlainDateTime'],
    [Temporal.PlainTime, 'toPlainTime'],
    [Temporal.PlainYearMonth, 'toPlainYearMonth']
  ]).get(TargetTemporalClass)

  if (typeof input === 'string') {
    input = getTemporalInstanceFromISOString(input) as ISOStringInputs<InstanceType<TClass>>
    if (input == null) {
      // eslint-disable-next-line no-console
      console.error(`Invalid ISO String "${input}", attempted to convert with ${conversionFn?.slice(2)}.toString()`)
    }
  }

  if (input == null || typeof input === 'string') return ''

  if (input instanceof TargetTemporalClass) {
    return input.toString()
  }

  if (conversionFn == null) return null

  if (input instanceof Date || typeof input === 'number') {
    if (!timeZone) return ''
    return toTemporalInstant.call(new Date(input)).toZonedDateTimeISO(timeZone)[conversionFn]().toString()
  }

  if (input instanceof Temporal.Instant) {
    if (!timeZone) return ''
    return input.toZonedDateTimeISO(timeZone)[conversionFn]().toString()
  }

  if (
    TargetTemporalClass === Temporal.PlainDate &&
    (input instanceof Temporal.ZonedDateTime || input instanceof Temporal.PlainDateTime)
  ) {
    return input.toPlainDate().toString()
  }

  if (
    TargetTemporalClass === Temporal.PlainDateTime &&
    (input instanceof Temporal.ZonedDateTime || input instanceof Temporal.PlainDate)
  ) {
    return input.toPlainDateTime().toString()
  }

  if (
    TargetTemporalClass === Temporal.PlainTime &&
    (input instanceof Temporal.ZonedDateTime || input instanceof Temporal.PlainDateTime)
  ) {
    return input.toPlainTime().toString()
  }

  if (
    TargetTemporalClass === Temporal.PlainYearMonth &&
    (input instanceof Temporal.ZonedDateTime ||
      input instanceof Temporal.PlainDateTime ||
      input instanceof Temporal.PlainDate)
  ) {
    return input.toPlainYearMonth().toString()
  }

  return null
}

/**
 * Returns a UTC ISO string from a Temporal object, Date, or number, e.g. "2021-01-31T15:30:00Z"
 *
 * It will always have the "Z", if you are looking for a string without the "Z" use toISO.dateTime
 * but be aware that you'll need to provide a timeZone.
 */
export function toISOInstantString(
  input: Date | number | Temporal.Instant | Temporal.ZonedDateTime | null | undefined
) {
  if (input == null) return ''

  if (input instanceof Temporal.Instant) {
    return input.toString()
  }

  if (input instanceof Date || typeof input === 'number') {
    return toTemporalInstant.call(new Date(input)).toString()
  }

  return input.toInstant().toString()
}

export function isTemporal(input: any): input is TemporalInstance {
  return (
    input instanceof Temporal.Instant ||
    input instanceof Temporal.PlainDate ||
    input instanceof Temporal.PlainDateTime ||
    input instanceof Temporal.PlainTime ||
    input instanceof Temporal.ZonedDateTime ||
    input instanceof Temporal.PlainYearMonth ||
    input instanceof Temporal.PlainMonthDay
  )
}

export function isPlainTemporal(
  input: any
): input is Exclude<TemporalInstance, Temporal.Instant | Temporal.ZonedDateTime> {
  return (
    input instanceof Temporal.PlainDate ||
    input instanceof Temporal.PlainDateTime ||
    input instanceof Temporal.PlainTime ||
    input instanceof Temporal.PlainYearMonth ||
    input instanceof Temporal.PlainMonthDay
  )
}

/**
 * Utility method wrapper around Temporal.PlainDate's equals method that includes null/undefined checks.
 * since Temporal's method throws an error if you pass in null or undefined.
 * @param day1
 * @param day2
 * @returns boolean
 */
export function isSameDay(day1: Temporal.PlainDate | null | undefined, day2: Temporal.PlainDate | null | undefined) {
  // if both are falsy then consider that the same day
  if (!day1 && !day2) return true
  if (!day1 || !day2) return false
  return day1.equals(day2)
}

export type TemporalInstanceOrHigher<T extends TemporalInstance> = T extends Temporal.Instant
  ? Temporal.Instant | Temporal.ZonedDateTime
  : T extends Temporal.ZonedDateTime
  ? Temporal.ZonedDateTime
  : T extends Temporal.PlainDateTime
  ? Temporal.PlainDateTime | Temporal.ZonedDateTime
  : T extends Temporal.PlainDate
  ? Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime
  : T extends Temporal.PlainTime
  ? Temporal.PlainTime | Temporal.PlainDateTime | Temporal.ZonedDateTime
  : T extends Temporal.PlainYearMonth
  ? Temporal.PlainYearMonth | Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime
  : T extends Temporal.PlainMonthDay
  ? Temporal.PlainMonthDay | Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime
  : never
